import {
  Annotation,
  GondolaChangeGroup,
} from "@/entities/gondola-change-groups/model/types";

import {
  GondolaChangeGroupsData,
  CartLineWithMetrics,
  SKUBaseId,
  ProductInfo,
  GondolaChange,
  SKUSectionChange,
  IntersectedProxExtra,
  ClosestGondolaDistItem,
} from "@/entities/gondola-change-groups/model/types";
import { FILTER_OPTIONS, FilterOption } from "./const";
import {
  TrackAvatarBinding,
  JourneyTrackRelation,
} from "@/entities/gondola-change-groups/model/slice";
import {
  HandProjectionData,
  Order,
  TrackPinsResponse,
} from "@/shared/api/lod-api";
import { TrackPin } from "@/features/sku-sections/components/camera-image";
import { AnnotationAction, HandId } from "@/entities/annotation";
import {
  STAGE_STATUS,
  ANNOTATION_STATUS,
  AnnotationStatusType,
} from "@/features/assignment/lib/types";
import { Dimensions } from "@/shared";

export interface AnnotationStatusResult {
  type: AnnotationStatusType;
  value: string;
}

/**
 * Combines checkout_lines and annotated_checkout_lines with tp_fp_fn_per_sku data
 * Creates a unified list of cart items with their performance metrics
 */
export function combineCartLinesWithMetrics(
  data: GondolaChangeGroupsData | null
): CartLineWithMetrics[] {
  if (
    !data ||
    !data.tp_fp_fn_per_sku ||
    Object.keys(data.tp_fp_fn_per_sku).length === 0
  )
    return [];

  const {
    checkout_lines = [],
    annotated_checkout_lines = [],
    tp_fp_fn_per_sku = {},
  } = data;

  const checkoutMap = new Map<SKUBaseId, ProductInfo>();
  const annotatedMap = new Map<SKUBaseId, ProductInfo>();

  checkout_lines.forEach((product) => {
    checkoutMap.set(product.sku_base_id, product);
  });

  annotated_checkout_lines.forEach((product) => {
    annotatedMap.set(product.sku_base_id, product);
  });

  const cartLines: CartLineWithMetrics[] = Object.entries(tp_fp_fn_per_sku)
    .map(([sku_base_id, metrics]) => {
      const annotatedProduct = annotatedMap.get(sku_base_id as SKUBaseId);
      const checkoutProduct = checkoutMap.get(sku_base_id as SKUBaseId);
      const productInfo = annotatedProduct || checkoutProduct;

      if (!productInfo) return null;

      const [tp, fp, fn] = metrics;

      return {
        sku_base_id: sku_base_id as SKUBaseId,
        barcode: productInfo.barcode,
        image_url: productInfo.image_url,
        name: productInfo.name,
        variant_id: productInfo.variant_id,
        quantity: productInfo.quantity,
        tp,
        fp,
        fn,
      };
    })
    .filter((item): item is CartLineWithMetrics => item !== null);

  return cartLines.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Generates a unique customId for a gondola change group based on gondola_id and time range
 */
export function generateGondolaChangeGroupCompositeId(
  group: GondolaChangeGroup
): string {
  return `${group.gondola_id}_${group.min_frame_start_time}_${group.max_frame_end_time}`;
}

/**
 * Generates a composite ID for gondola changes and sku section changes
 * using camera_local_id + frame_start_time + frame_end_time
 */
export function generateChangeCustomId(
  item: GondolaChange | SKUSectionChange
): string {
  return `${item.camera_local_id}_${item.frame_start_time}_${item.frame_end_time}`;
}

/**
 * Adds composite IDs to gondola_change_groups, gondola_changes and sku_section_changes within a data set
 */
export function addCompositeIdsToChangeGroups(
  data: GondolaChangeGroupsData
): GondolaChangeGroupsData {
  return {
    ...data,
    gondola_change_groups: data.gondola_change_groups.map((group) => ({
      ...group,
      gondola_change_group_composite_id:
        generateGondolaChangeGroupCompositeId(group),
      gondola_changes: group.gondola_changes.map((change) => ({
        ...change,
        gondola_change_camera_composite_id: generateChangeCustomId(change),
      })),
      sku_section_change_groups: group.sku_section_change_groups.map(
        (sscg) => ({
          ...sscg,
          sku_section_changes: sscg.sku_section_changes.map((change) => ({
            ...change,
            gondola_change_camera_composite_id: generateChangeCustomId(change),
          })),
        })
      ),
    })),
  };
}

export const applyRangeFilter = (
  filterConfig: { option: FilterOption; value?: number } | undefined,
  actualValue: number
): boolean => {
  if (!filterConfig || filterConfig.option === FILTER_OPTIONS.ALL) return true;
  if (filterConfig.option === FILTER_OPTIONS.ZERO) return actualValue === 0;
  if (filterConfig.option === FILTER_OPTIONS.RANGE) {
    return actualValue >= 1;
  }
  return true;
};

// Available animals in react-animals library
const AVAILABLE_ANIMALS = [
  "alligator",
  "anteater",
  "armadillo",
  "auroch",
  "axolotl",
  "badger",
  "bat",
  "beaver",
  "buffalo",
  "camel",
  "capybara",
  "chameleon",
  "cheetah",
  "chinchilla",
  "chipmunk",
  "chupacabra",
  "cormorant",
  "coyote",
  "crow",
  "dingo",
  "dinosaur",
  "dolphin",
  "duck",
  "elephant",
  "ferret",
  "fox",
  "frog",
  "giraffe",
  "gopher",
  "grizzly",
  "hedgehog",
  "hippo",
  "hyena",
  "ibex",
  "ifrit",
  "iguana",
  "jackal",
  "kangaroo",
  "koala",
  "kraken",
  "lemur",
  "leopard",
  "liger",
  "llama",
  "manatee",
  "mink",
  "monkey",
  "moose",
  "narwhal",
  "orangutan",
  "otter",
  "panda",
  "penguin",
  "platypus",
  "pumpkin",
  "python",
  "quagga",
  "rabbit",
  "raccoon",
  "rhino",
  "sheep",
  "shrew",
  "skunk",
  "squirrel",
  "tiger",
  "turtle",
  "walrus",
  "wolf",
  "wolverine",
  "wombat",
];

// Note: Currently using only green color for all avatars
// Red color will be used conditionally in future implementations

/**
 * Better hash function for large track IDs
 * Uses string-based djb2 hash algorithm for better distribution
 *
 * @param trackId The track ID to hash
 * @returns A positive integer hash value
 */
function hashTrackId(trackId: number): number {
  const str = trackId.toString();
  let hash = 5381; // djb2 algorithm magic number (proven effective)

  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash + str.charCodeAt(i); // hash * 33 + c
    hash = hash & hash; // Convert to 32-bit integer
  }

  return Math.abs(hash);
}

/**
 * Generates a hash-based avatar for consistency
 * This is used as the primary assignment method
 */
function generateHashBasedAvatar(
  trackId: number,
  color = "green"
): TrackAvatarBinding {
  // Use hash function for animal selection
  const animalHash = hashTrackId(trackId);
  const animalIndex = animalHash % AVAILABLE_ANIMALS.length;

  return {
    trackId,
    animalName: AVAILABLE_ANIMALS[animalIndex],
    color,
  };
}

// New helper functions for journey and track processing

/**
 * Processes track_ids_per_journey_id and recovered_track_ids_per_track_id
 * to create journey track relations with only valid track IDs that exist in recovery mapping
 */
export function processJourneyTrackRelations(
  trackIdsPerJourneyId: Record<string, number[]>,
  recoveredTrackIdsPerTrackId: Record<string, number[]>
): JourneyTrackRelation[] {
  const journeyTrackRelations: JourneyTrackRelation[] = [];

  Object.entries(trackIdsPerJourneyId).forEach(([journeyId, trackIds]) => {
    const validTrackIds: number[] = [];
    const allRecoveredTrackIds: number[] = [];

    trackIds.forEach((trackId) => {
      const trackIdStr = String(trackId);

      if (recoveredTrackIdsPerTrackId[trackIdStr]) {
        validTrackIds.push(trackId);
        const recoveredIds = recoveredTrackIdsPerTrackId[trackIdStr];
        allRecoveredTrackIds.push(...recoveredIds);
      }
    });

    if (validTrackIds.length > 0) {
      journeyTrackRelations.push({
        journeyId,
        trackIds: validTrackIds,
        recoveredTrackIds: Array.from(new Set(allRecoveredTrackIds)),
      });
    }
  });

  return journeyTrackRelations;
}

/**
 * Extracts all unique track IDs in occlusion from gondola change groups
 * Excludes track IDs that are already covered by journey track relations
 */
export function extractTrackIdsInOcclusion(
  gondolaChangeGroups: GondolaChangeGroup[],
  journeyTrackRelations: JourneyTrackRelation[]
): number[] {
  const trackIdsInOcclusion = new Set<number>();

  // Get all track IDs that are covered by journey relations
  const journeyCoveredTrackIds = new Set<number>();

  journeyTrackRelations.forEach((relation) => {
    // Add main track IDs from journey
    relation.trackIds.forEach((trackId) => {
      journeyCoveredTrackIds.add(trackId);
    });

    // Add all recovered track IDs from journey
    relation.recoveredTrackIds.forEach((recoveredTrackId) => {
      journeyCoveredTrackIds.add(recoveredTrackId);
    });
  });

  // Extract track IDs from occlusion, excluding journey-covered track IDs
  gondolaChangeGroups.forEach((group) => {
    group.sku_section_change_groups.forEach((sscg) => {
      sscg.track_ids_in_occlusion.forEach((trackId) => {
        // Only add if not covered by journey relations
        if (!journeyCoveredTrackIds.has(trackId)) {
          trackIdsInOcclusion.add(trackId);
        }
      });
    });
  });

  return Array.from(trackIdsInOcclusion);
}

/**
 * Creates a mapping from recovered track IDs to their original track IDs
 */
export function createRecoveredTrackIdMapping(
  recoveredTrackIdsPerTrackId: Record<string, number[]>
): Record<number, number> {
  const mapping: Record<number, number> = {};

  Object.entries(recoveredTrackIdsPerTrackId).forEach(
    ([originalTrackIdStr, recoveredTrackIds]) => {
      const originalTrackId = Number(originalTrackIdStr);

      recoveredTrackIds.forEach((recoveredTrackId) => {
        mapping[recoveredTrackId] = originalTrackId;
      });
    }
  );

  return mapping;
}

/**
 * Creates comprehensive track avatar bindings based on journey track IDs and track IDs in occlusion
 * Prioritizes journey track IDs over track IDs in occlusion
 * Uses color coding: Journey track IDs = GREEN, Track IDs in occlusion = RED
 * Also creates direct bindings for recovered track IDs that share the same avatar as their original track ID
 */
export function createComprehensiveTrackAvatarBindings(
  journeyTrackRelations: JourneyTrackRelation[],
  trackIdsInOcclusion: number[]
): Record<number, TrackAvatarBinding> {
  const bindings: Record<number, TrackAvatarBinding> = {};
  const usedAnimals = new Set<string>();

  // Get all journey track IDs for prioritization
  const journeyTrackIds = new Set<number>();
  journeyTrackRelations.forEach((relation) => {
    relation.trackIds.forEach((trackId) => {
      journeyTrackIds.add(trackId);
    });
  });

  // First, create bindings for journey track IDs (highest priority) - GREEN color
  journeyTrackIds.forEach((trackId) => {
    const binding = generateUniqueAvatar(trackId, usedAnimals, "green");
    bindings[trackId] = binding;
    usedAnimals.add(binding.animalName);
  });

  // Then, create bindings for recovered track IDs - same avatar as their original track ID
  journeyTrackRelations.forEach((relation) => {
    relation.trackIds.forEach((originalTrackId) => {
      const originalBinding = bindings[originalTrackId];
      if (originalBinding) {
        // Add direct bindings for all recovered track IDs with the same avatar
        relation.recoveredTrackIds.forEach((recoveredTrackId) => {
          bindings[recoveredTrackId] = {
            trackId: recoveredTrackId,
            animalName: originalBinding.animalName,
            color: originalBinding.color,
          };
        });
      }
    });
  });

  // Finally, create bindings for track IDs in occlusion - RED color
  trackIdsInOcclusion.forEach((trackId) => {
    // Only create binding if it doesn't already exist (avoid overriding journey/recovered bindings)

    const binding = generateUniqueAvatar(trackId, usedAnimals, "red");
    bindings[trackId] = binding;
    usedAnimals.add(binding.animalName);
  });

  return bindings;
}

/**
 * Generates a unique avatar that hasn't been used yet
 */
function generateUniqueAvatar(
  trackId: number,
  usedAnimals: Set<string>,
  color: string
): TrackAvatarBinding {
  let attempts = 0;
  const maxAttempts = 1000;

  while (attempts < maxAttempts) {
    const candidate = generateHashBasedAvatar(trackId + attempts, color);

    if (!usedAnimals.has(candidate.animalName)) {
      return candidate;
    }

    attempts++;
  }

  // Fallback: use sequential assignment if no unique combination found
  const animalIndex = trackId % AVAILABLE_ANIMALS.length;

  return {
    trackId,
    animalName: AVAILABLE_ANIMALS[animalIndex],
    color,
  };
}

/**
 * Finds avatar binding for any track ID (original or recovered)
 * If the input is a recovered track ID, it finds the original track ID and returns its binding
 */
export function findTrackAvatarBinding(
  trackId: number,
  trackAvatarBindings: Record<number, TrackAvatarBinding>,
  recoveredTrackIdMapping: Record<number, number>
): TrackAvatarBinding | null {
  // First, check if direct binding exists
  if (trackAvatarBindings[trackId]) {
    return trackAvatarBindings[trackId];
  }

  // If not found, check if this is a recovered track ID
  const originalTrackId = recoveredTrackIdMapping[trackId];
  if (originalTrackId && trackAvatarBindings[originalTrackId]) {
    return trackAvatarBindings[originalTrackId];
  }

  // No binding found
  return null;
}

/**
 * Detects track IDs that are linked to multiple journey IDs
 * Returns an array of track IDs that appear in more than one journey relation
 */
export function detectTracksWithMultipleJourneys(
  journeyTrackRelations: JourneyTrackRelation[]
): number[] {
  const trackJourneyMap = new Map<number, Set<string>>();

  journeyTrackRelations.forEach((relation) => {
    relation.trackIds.forEach((trackId) => {
      if (!trackJourneyMap.has(trackId)) {
        trackJourneyMap.set(trackId, new Set());
      }
      const journeySet = trackJourneyMap.get(trackId);
      if (journeySet) {
        journeySet.add(relation.journeyId);
      }
    });
  });

  const multipleJourneyTracks: number[] = [];
  trackJourneyMap.forEach((journeyIds, trackId) => {
    if (journeyIds.size > 1) {
      multipleJourneyTracks.push(trackId);
    }
  });

  return multipleJourneyTracks;
}

/**
 * Processes track pins data from API response and transforms it to TrackPin array
 * Filters out pins that are outside image boundaries and applies avatar bindings
 */
export function processTrackPinsData(
  trackPinsResponse: TrackPinsResponse,
  avatarBindings: Record<number, TrackAvatarBinding>,
  imageDimensions: Dimensions,
  currentCameraId: string | number | null,
  isValidContext: boolean
): TrackPin[] {
  if (!isValidContext || !currentCameraId || !trackPinsResponse?.result) {
    return [];
  }

  const { pin_points_2d, track_ids } = trackPinsResponse.result;

  return track_ids
    .map((trackId: number, index: number) => {
      const [x, y] = pin_points_2d[index];
      const avatarBinding = avatarBindings[trackId];

      if (
        x < 0 ||
        x > imageDimensions.width ||
        y < 0 ||
        y > imageDimensions.height
      ) {
        return null;
      }

      return avatarBinding
        ? {
            trackId,
            x,
            y,
            animalName: avatarBinding.animalName,
            color: avatarBinding.color,
          }
        : { trackId, x, y };
    })
    .filter(Boolean) as TrackPin[];
}

export const countAnnotationsType = (
  annotations: Annotation[],
  type: AnnotationAction
) => {
  return (
    annotations
      ?.filter((annotation) => annotation.update_type === type)
      .reduce((balance, annotation) => balance + annotation.count, 0) || 0
  );
};

export function transformIntersectedProxExtra(
  trackId: number,
  handId: HandId,
  extraData: string
): ClosestGondolaDistItem[] {
  try {
    const parsedExtra: IntersectedProxExtra = JSON.parse(extraData);
    const {
      closest_gondola_dist_times_topk,
      closest_gondola_dist_indices_topk,
    } = parsedExtra;

    const cameraIds = Object.keys(
      parsedExtra.projected_wrist_points_2d_per_cam
    );

    const transformCameraData = <T>(
      sourceData: Record<string, T[]>,
      index: number
    ): Record<string, T> =>
      Object.fromEntries(
        cameraIds.map((cameraId) => [cameraId, sourceData[cameraId][index]])
      );

    return closest_gondola_dist_times_topk.map((timestamp, index) => ({
      id: `${trackId}_${timestamp}`,
      track_id: trackId,
      hand_id: handId,
      timestamp,
      index,
      closest_gondola_dist_index: closest_gondola_dist_indices_topk[index],
      projected_wrist_points_2d_per_cam: transformCameraData(
        parsedExtra.projected_wrist_points_2d_per_cam,
        index
      ),
      sku_ids_from_sku_sections_per_cam: transformCameraData(
        parsedExtra.sku_ids_from_sku_sections_per_cam,
        index
      ),
      sku_section_intersections_per_cam: transformCameraData(
        parsedExtra.sku_section_intersections_per_cam,
        index
      ),
      projected_gondola_points_2d_per_cam: transformCameraData(
        parsedExtra.projected_gondola_points_2d_per_cam,
        index
      ),
      sku_section_ids_from_sku_sections_per_cam: transformCameraData(
        parsedExtra.sku_section_ids_from_sku_sections_per_cam,
        index
      ),
    }));
  } catch (error) {
    console.error("Error transforming intersected prox extra data:", error);
    return [];
  }
}

export const getAnnotationStatus = (
  order: Order | null
): AnnotationStatusResult => {
  if (!order) return { type: ANNOTATION_STATUS.NOT_STARTED, value: "-" };

  if (order.anomaly_report && order.anomaly_report.trim()) {
    return { type: ANNOTATION_STATUS.ANOMALY, value: "Anomaly Reported" };
  }

  const stageStatuses = order.stage_detail_statuses || [];

  const stageOrder = [
    { code: "journey_annotation", label: "Journey Annotation" },
    { code: "transaction_annotation", label: "Transaction Annotation" },
    {
      code: "track_persistence_annotation",
      label: "Track Persistence Annotation",
    },
  ];

  let latestCompletedStage = null;

  for (const stage of stageOrder) {
    const stageStatus = stageStatuses.find((s) => s.stage === stage.code);

    if (stageStatus?.status === STAGE_STATUS.REJECTED) {
      return {
        type: ANNOTATION_STATUS.REJECTED,
        value: `${stage.label} Rejected`,
      };
    }

    if (stageStatus?.status === STAGE_STATUS.DONE) {
      latestCompletedStage = stage;
    }
  }

  if (latestCompletedStage) {
    return {
      type: ANNOTATION_STATUS.DONE,
      value: `${latestCompletedStage.label} Done`,
    };
  }

  return { type: ANNOTATION_STATUS.NOT_STARTED, value: "Not Started" };
};

export const findClosestHandProjection = (
  projections: HandProjectionData[],
  currentTimeMs: number
): HandProjectionData | null => {
  if (!projections.length || isNaN(currentTimeMs) || currentTimeMs <= 0) {
    return null;
  }

  let left = 0;
  let right = projections.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midTime = new Date(projections[mid].track_time).getTime();

    if (midTime === currentTimeMs) {
      return projections[mid];
    } else if (midTime < currentTimeMs) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  if (left >= projections.length) {
    return null;
  }

  if (right < 0) {
    return null;
  }

  const leftTime = new Date(projections[left].track_time).getTime();
  const rightTime = new Date(projections[right].track_time).getTime();

  const leftDiff = Math.abs(leftTime - currentTimeMs);
  const rightDiff = Math.abs(rightTime - currentTimeMs);

  return leftDiff <= rightDiff ? projections[left] : projections[right];
};
